import { useMutation } from '@apollo/client';

import { Role, TeamMemberRole } from '__generated__/GQL';
import useCompanyContext from 'common/hooks/useCompanyContext';

import { refetchTeamMembersQuery } from '../../../../hooks/useTeamMembers';
import { refetchTeamMembersInvitationQuotaQuery } from '../../../../hooks/useTeamMembersInvitationQuota';
import { refetchQuickInvitationsQuery } from '../useQuickInvitations';
import { GiveLegalBeneficiaryAccessDocument } from './giveLegalBeneficiaryAccess.gql';

const mapTeamMemberRoleToRole = (role: TeamMemberRole): Role => {
  if (role === TeamMemberRole.Admin) {
    return Role.Admin;
  }
  if (role === TeamMemberRole.Accountant) {
    return Role.Accountant;
  }
  if (role === TeamMemberRole.Employee) {
    return Role.Employee;
  }
  throw new Error('InviteTeamMemberMutation used with an unhandled role');
};

export const useGiveLegalBeneficiaryAccess = () => {
  const { companyProfileId } = useCompanyContext();

  if (!companyProfileId) {
    throw new Error(
      "'companyProfileId' is undefined in 'useGiveLegalBeneficiaryAccess'",
    );
  }

  const [mutation, hooks] = useMutation(GiveLegalBeneficiaryAccessDocument);

  const giveLegalBeneficiaryAccess = ({
    companyUserId,
    role,
  }: {
    companyUserId: string;
    role: TeamMemberRole;
  }) =>
    mutation({
      refetchQueries: [
        refetchTeamMembersQuery(companyProfileId),
        refetchQuickInvitationsQuery(companyProfileId),
        refetchTeamMembersInvitationQuotaQuery(companyProfileId),
      ],
      variables: {
        input: {
          companyProfileId,
          companyUserId,
          role: mapTeamMemberRoleToRole(role),
        },
      },
    });

  return [giveLegalBeneficiaryAccess, hooks] as const;
};
